import { useTranslation } from 'react-i18next';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Check, Layers, LayoutList, ListChecks, LucideIcon, SlidersHorizontal } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import useOrdersExport from '@/hooks/useOrdersExport';
import { ExportScope, formatExportCount, formatExportRange } from '@/utils/helpers/orderExport';

type OrdersExportState = ReturnType<typeof useOrdersExport>;

/** Uppercase muted section label, the house style for modal sections. */
export function SectionLabel({ children }: { children: string }) {
  return (
    <span className='text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
      {children}
    </span>
  );
}

interface ScopeCardProps {
  scope: ExportScope;
  icon: LucideIcon;
  title: string;
  /** Muted lines above the count, e.g. the tab name, then the period. */
  subtitle?: string[];
  /** The count line; undefined = still counting (a shimmer, never "0"). */
  countText?: string;
  /** A fixed muted line instead of a count. */
  hint?: string;
}

function ScopeCard({ scope, icon: Icon, title, subtitle, countText, hint }: ScopeCardProps) {
  const { t } = useTranslation();

  return (
    <RadioGroupPrimitive.Item
      value={scope}
      className={cn(
        'group relative flex min-h-[76px] w-full cursor-pointer items-start gap-3 rounded-2xl border border-solid border-sd-border bg-white p-3.5 text-start',
        'transition-all duration-150 hover:-translate-y-px hover:border-sd-foreground/25 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        'data-[state=checked]:border-sd-primary data-[state=checked]:bg-sd-primary/10 data-[state=checked]:ring-2 data-[state=checked]:ring-sd-primary',
      )}
    >
      <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sd-muted text-sd-foreground transition-colors group-data-[state=checked]:bg-sd-primary'>
        <Icon className='h-[18px] w-[18px]' aria-hidden />
      </span>
      <span className='flex min-w-0 flex-1 flex-col gap-1 pe-5'>
        <span className='text-sm font-semibold leading-5 text-sd-foreground'>{title}</span>
        {subtitle?.map((line) => (
          <span
            key={line}
            className='truncate text-xs leading-4 text-sd-muted-foreground'
            title={line}
          >
            {line}
          </span>
        ))}
        <span className='flex min-h-4 flex-wrap items-center gap-x-1 text-xs leading-4 text-sd-muted-foreground'>
          {hint ?? countText ?? (
            <>
              <Skeleton aria-hidden className='h-3 w-14 rounded-full bg-sd-muted' />
              <span className='sr-only'>{String(t('export_modal.counting'))}</span>
            </>
          )}
        </span>
      </span>
      <RadioGroupPrimitive.Indicator className='absolute end-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-sd-primary text-sd-foreground shadow-sm'>
        <Check className='h-3 w-3' strokeWidth={3} aria-hidden />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

interface ExportScopeCardsProps {
  state: OrdersExportState;
  disabled: boolean;
}

/**
 * BOXY-409 — "What to export" as keyboard-accessible radio cards (arrow keys
 * move and select). "Selected" shows only while rows are ticked.
 */
export function ExportScopeCards({ state, disabled }: ExportScopeCardsProps) {
  const { t, i18n } = useTranslation();
  const label = String(t('export_modal.what_to_export'));

  const countText = (scope: ExportScope) => {
    const count = state.counts[scope];
    return count === undefined
      ? undefined
      : String(t('export_modal.n_orders', { n: formatExportCount(count, i18n.language) }));
  };

  // The period every card but ticked uids exports, on its own line under the card's name.
  const { preset, from, to } = state;
  const period =
    preset !== 'custom'
      ? String(t(`export_modal.period_${preset}`))
      : from && to
        ? formatExportRange(from, to, i18n.language)
        : '';
  const withPeriod = (text?: string) => [text, period].filter((line): line is string => !!line);

  return (
    <div className='flex flex-col gap-2.5'>
      <SectionLabel>{label}</SectionLabel>
      <RadioGroupPrimitive.Root
        value={state.scope}
        onValueChange={(value) => state.setScope(value as ExportScope)}
        disabled={disabled}
        aria-label={label}
        className={cn(
          'grid gap-3',
          // Three across: icon above the text so titles do not wrap.
          state.hasSelection ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-3 sm:[&>button]:flex-col',
        )}
      >
        {state.hasSelection ? (
          <ScopeCard
            scope='selected'
            icon={ListChecks}
            title={String(t('export_modal.card_selected'))}
            subtitle={
              state.selection.isAllSelected
                ? withPeriod(
                    String(
                      t('export_modal.all_n_selected', {
                        n: formatExportCount(state.selection.count, i18n.language),
                      }),
                    ),
                  )
                : undefined
            }
            countText={countText('selected')}
          />
        ) : null}
        <ScopeCard
          scope='tab'
          icon={LayoutList}
          title={String(t('export_modal.card_tab'))}
          subtitle={withPeriod(state.tabLabel)}
          countText={countText('tab')}
        />
        <ScopeCard
          scope='all'
          icon={Layers}
          title={String(t('export_modal.scope_all'))}
          subtitle={withPeriod()}
          countText={countText('all')}
        />
        <ScopeCard
          scope='custom'
          icon={SlidersHorizontal}
          title={String(t('export_modal.scope_custom'))}
          subtitle={[String(t('export_modal.custom_hint'))]}
          hint={period}
        />
      </RadioGroupPrimitive.Root>
    </div>
  );
}
